import styled, { css } from 'styled-components';
import { horizontaFluidPaddings, medias } from '@/utils/sizes';
import { TImagePosition } from './HeroProject';
import { generateClampFromMinMax } from '@/utils/styling';

export const ContentContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['contentJustify', 'paddingLeft', 'paddingRight'].includes(prop),
})<{
  paddingLeft: string;
  paddingRight: string;
  contentJustify: string;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-component-inner);
  justify-content: ${({ contentJustify }) => contentJustify};

  ${medias.max.tablet} {
    padding-left: 0;
    padding-right: 0;
  }

  iframe {
    background-color: var(--c-black) !important;
  }
`;

export const Breadcrumb = styled.div`
  position: relative;
  display: inline-block;
  grid-row-start: 1;
  grid-column-start: 1;
  height: min-content;
  white-space: nowrap;
  width: min-content;

  ${medias.max.mobile} {
    a {
      display: block;
      margin-bottom: var(--spacing-component-inner);
      font-size: var(--fs-lg);
      letter-spacing: -0.01rem;
    }

    h1 {
      font-size: var(--fs-4xl);
      letter-spacing: -0.02rem;
    }
  }

  .link-edit-project {
    position: absolute;
    color: var(--c-font-secondary);
    z-index: 5;
    bottom: -3px;
    right: -40px;
    padding-left: 10px;
    height: 40px;
    width: 40px;
    font-style: italic;
    opacity: 0;
    transition: opacity var(--tr-base) ease-in-out;

    img {
      height: 30px;
      width: 30px;
    }
  }

  h1 {
    width: min-content;
  }

  &:hover .link-edit-project {
    opacity: 0.7;
  }
`;

export const ImageContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['imageAspectRatio', 'imageSide', 'mobileHorizontalPadding'].includes(
      prop
    ),
})<{
  imageAspectRatio: string;
  mobileHorizontalPadding: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  aspect-ratio: ${({ imageAspectRatio }) => imageAspectRatio};

  &.hero-image-container {
    cursor: zoom-in;
  }
`;

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      'imagePosition',
      'imageAspectRatio',
      'imageMarginBottomOnMobile',
    ].includes(prop),
})<{
  imagePosition: TImagePosition;
  imageAspectRatio: string;
  imageMarginBottomOnMobile: boolean;
}>`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${({ imagePosition }) =>
    imagePosition === 'right'
      ? 'calc(50% - var(--spacing-component-inner)) var(--spacing-component-inner) calc(50% - var(--spacing-component-inner))'
      : '1fr'};

  ${medias.max.tablet} {
    display: flex;
    flex-direction: column;
  }

  ${ContentContainer} {
    grid-row-start: ${({ imagePosition }) =>
      imagePosition === 'right' ? 1 : 2};
    grid-column: ${({ imagePosition }) =>
      imagePosition === 'right' ? '1 / 3' : 1};

    ${medias.min.desktopSm} {
      ${({ imagePosition }) =>
        imagePosition === 'right' &&
        // Centers component with the same padding right and left:
        css`
          box-sizing: border-box;
          padding-left: ${horizontaFluidPaddings.project.content};
          width: calc(100% + ${horizontaFluidPaddings.project.main});
          margin-left: calc(${horizontaFluidPaddings.project.main} * -1);
          padding-right: ${horizontaFluidPaddings.project.content};
          padding-top: var(--spacing-component);
        `}
    }
  }
  ${ImageContainer} {
    grid-row-start: ${({ imagePosition }) =>
      imagePosition === 'right' ? 1 : 2};
    grid-column-start: ${({ imagePosition }) =>
      imagePosition === 'right' ? 3 : 1};

    iframe {
      background: var(--c-black);
    }
  }

  ${({ imagePosition }) =>
    imagePosition === 'fullwidth'
      ? css`
          ${Breadcrumb} {
            margin-bottom: var(--spacing-component-inner);
          }
        `
      : css`
          ${ImageContainer} div:has(iframe) {
            aspect-ratio: 1/ 1;

            iframe {
              background: var(--c-black);
            }
          }
        `}

  ${medias.only.tablet} {
    &[data-layout='right'] {
      ${ImageContainer} {
        margin-bottom: var(--spacing-component);
      }
    }
  }

  ${medias.max.tablet} {
    ${Breadcrumb} {
      order: 1;
      margin-bottom: var(--spacing-component-inner);
    }

    ${ImageContainer} {
      order: 2;
    }

    ${ContentContainer} {
      order: 3;
    }
  }

  ${medias.max.mobile} {
    margin-bottom: var(--spacing-component);

    ${ImageContainer} {
      margin-bottom: ${({ imageMarginBottomOnMobile }) =>
        imageMarginBottomOnMobile ? 'var(--spacing-component-inner)' : '0'};
    }

    .fullwidth-mobile {
      width: 100vw !important;
      margin-left: calc(var(--page-pd-mobile-tablet) * -1) !important;
    }
  }
`;

export const InfoColumnsContainer = styled.div`
  width: 100%;
  height: 100%;

  .info-text {
    padding-top: ${generateClampFromMinMax(30, 65)};
  }

  ${medias.max.tablet} {

    .info-text {
      padding-top: 0;
    }

    p:not(.table-key, .table-value) {
      padding: 12px 0;
    }

    .info-columns .info-column:first-child {
      margin-bottom: var(--spacing-component-inner);
    }

    .info-column p {
      padding: 0;
    }

    .info-column {
      padding-bottom: var(--info-column-spacing-mobile);

      &:first-child {
        padding-top: 0;
        padding-bottom: 10px;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }

  ${medias.max.mobile} {
    padding-bottom: var(--spacing-component);
  }
`
